import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Checkbox,
    Combobox,
    FileInput,
    Grid,
    Group,
    Loader,
    Modal,
    Paper,
    ScrollArea,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    TagsInput,
    Text,
    TextInput,
    Textarea,
    useCombobox,
} from '@mantine/core';
import {
    DATA_FILE_STATUSES,
    DATA_FILE_TYPES,
} from '../../../common/enums/enums';
import {
    getDatafiles,
    getMediaGroups,
    getUserMediaGroups,
    getUsers,
} from '../../../../http/apiClient';
import { getEnumColor, getEnumLabel } from '../../../../utils/enumUtils';
import { useEffect, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconChevronRight } from '@tabler/icons-react';
import { SearchableSelect } from '../../../common';
import classes from './Transferlist.module.css';
import { countVariations } from '../../../../utils/text';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { genericValidator } from '../../../../utils/validation';
import { hasRole } from '../../../../utils/roles';
import useAddDataset from '../../../../http/mutations/data/useAddDataset';
import useAddMessageGroup from '../../../../http/mutations/groups/useAddMessageGroup';
import { useAuthStore } from '../../../../store';
import { useDebouncedCallback } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { usePaginatedQuery } from '../../../../http/queries/usePaginatedQuery';
import { useState } from 'react';
import useUpdateDataSetFiles from '../../../../http/mutations/data/useUpdateDataSetFiles';

/* ------------------------------------------------------------------ */
/* Demo data                                                           */
const fruits = [{ label: 'test2', value: 2 }];
const vegetables = [{ label: 'test', value: 1 }];

const RenderQueryList = ({
    options,
    renderOption = null,
    onTransfer,
    type,
    onSearchChange,
    isLoading,
}) => {
    const combobox = useCombobox();
    const [value, setValue] = useState([]); // selected items in this list
    const [search, setSearch] = useState(''); // search box text

    const handleValueSelect = (val) =>
        setValue((curr) =>
            curr.includes(val) ? curr.filter((v) => v !== val) : [...curr, val]
        );

    /* Filter & map to Combobox.Option rows */
    const items = options
        /*.filter((item) =>
            item.toLowerCase().includes(search.toLowerCase().trim())
        )*/
        .map((item) => (
            <Combobox.Option
                value={item.value}
                key={item.value}
                active={value.includes(item.value)}
                onMouseOver={() => combobox.resetSelectedOption()}>
                <Group gap='sm'>
                    {/* Checkbox is purely visual; selection handled by Combobox */}
                    <Checkbox
                        checked={value.includes(item.value)}
                        onChange={() => {}}
                        aria-hidden='true'
                        tabIndex={-1}
                        style={{ pointerEvents: 'none' }}
                    />
                    {renderOption ? (
                        renderOption(item)
                    ) : (
                        <span>{item.label}</span>
                    )}
                </Group>
            </Combobox.Option>
        ));

    return (
        <Grid.Col
            span={6}
            className={classes.renderList}
            data-type={type}>
            <Combobox
                store={combobox}
                onOptionSubmit={handleValueSelect}>
                {/* Search bar + arrow button */}
                <Combobox.EventsTarget>
                    <Group
                        wrap='nowrap'
                        gap={0}
                        className={classes.controls}>
                        <TextInput
                            placeholder='Search'
                            classNames={{ input: classes.input }}
                            value={search}
                            w={'100%'}
                            onChange={(e) => {
                                setSearch(e.currentTarget.value);
                                combobox.updateSelectedOptionIndex();
                                if (onSearchChange) {
                                    onSearchChange(e.currentTarget.value);
                                }
                            }}
                        />

                        <ActionIcon
                            radius={0}
                            variant='default'
                            size={36}
                            className={classes.control}
                            onClick={() => {
                                onTransfer(value); // send selected items to other list
                                setValue([]); // reset local selection
                            }}>
                            <IconChevronRight className={classes.icon} />
                        </ActionIcon>
                    </Group>
                </Combobox.EventsTarget>

                {/* Scrollable list */}
                <div className={classes.list}>
                    <ScrollArea
                        style={{ flex: 1 }}
                        viewportProps={{ style: { height: 200 } }}>
                        <Combobox.Options>
                            {items.length ? (
                                items
                            ) : isLoading ? (
                                <Group
                                    w={'100%'}
                                    align={'center'}
                                    justify={'center'}
                                    grow>
                                    <Loader size={'xs'} />
                                </Group>
                            ) : (
                                <Combobox.Empty>Nothing found…</Combobox.Empty>
                            )}
                        </Combobox.Options>
                    </ScrollArea>
                </div>
            </Combobox>
        </Grid.Col>
    );
};

const RenderSelectedList = ({
    options,
    onTransfer,
    type,
    renderOption = null,
}) => {
    const combobox = useCombobox();
    const [value, setValue] = useState(options.map((x) => x.value)); // selected items in this list
    const [search, setSearch] = useState(''); // search box text

    const handleValueSelect = (val) =>
        setValue((curr) =>
            curr.includes(val) ? curr.filter((v) => v !== val) : [...curr, val]
        );

    /* Filter & map to Combobox.Option rows */
    const items = options
        .filter((item) =>
            item.label.toLowerCase().includes(search.toLowerCase().trim())
        )
        .map((item) => (
            <Combobox.Option
                value={item.value}
                key={item.value}
                active={value.includes(item.value)}
                onMouseOver={() => combobox.resetSelectedOption()}>
                <Group gap='sm'>
                    {/* Checkbox is purely visual; selection handled by Combobox */}
                    <Checkbox
                        checked={value.includes(item.value)}
                        onChange={() => {}}
                        aria-hidden='true'
                        tabIndex={-1}
                        style={{ pointerEvents: 'none' }}
                    />
                    {renderOption ? (
                        renderOption(item)
                    ) : (
                        <span>{item.label}</span>
                    )}
                </Group>
            </Combobox.Option>
        ));

    return (
        <Grid.Col
            span={6}
            className={classes.renderList}
            data-type={type}>
            <Combobox
                store={combobox}
                onOptionSubmit={handleValueSelect}>
                {/* Search bar + arrow button */}
                <Combobox.EventsTarget>
                    <Group
                        wrap='nowrap'
                        gap={0}
                        className={classes.controls}>
                        <TextInput
                            placeholder='Search'
                            classNames={{ input: classes.input }}
                            value={search}
                            w={'100%'}
                            onChange={(e) => {
                                setSearch(e.currentTarget.value);
                                combobox.updateSelectedOptionIndex();
                                if (onSearchChange) {
                                    onSearchChange(e.currentTarget.value);
                                }
                            }}
                        />

                        <ActionIcon
                            radius={0}
                            variant='default'
                            size={36}
                            className={classes.control}
                            onClick={() => {
                                onTransfer(value); // send selected items to other list
                                setValue([]); // reset local selection
                            }}>
                            <IconChevronRight className={classes.icon} />
                        </ActionIcon>
                    </Group>
                </Combobox.EventsTarget>

                {/* Scrollable list */}
                <div className={classes.list}>
                    <ScrollArea
                        style={{ flex: 1 }}
                        viewportProps={{ style: { height: 200 } }}>
                        <Combobox.Options>
                            {items.length ? (
                                items
                            ) : (
                                <Combobox.Empty>Nothing found…</Combobox.Empty>
                            )}
                        </Combobox.Options>
                    </ScrollArea>
                </div>
            </Combobox>
        </Grid.Col>
    );
};

/* ------------------------------------------------------------------ */
/* TransferList — container that holds two RenderList components       */
const TransferList = ({
    queryHttpFn,
    queryKey,
    optionsMapFn,
    defaultOptions,
    renderOption,
    form,
    onChange,
    pageSize = 200,
}) => {
    const [currentValues, setCurrentValues] = useState([]);
    const [optionCache, setOptionCache] = useState(defaultOptions || []);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize,
    });
    const filterDebounce = useDebouncedCallback((query) => {
        setGlobalFilter(query);
    }, 600);

    const {
        isLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) => queryHttpFn(queryParams),
        queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
        refetchInterval: 15_0000,
        globalFilter,
        filters: [],
        pagination,
        sorting: [],
    });

    const { data: queryData, pagination: queryPagination } =
        paginatedData || {};

    useEffect(() => {
        setOptionCache(defaultOptions);
        if (defaultOptions) {
            setCurrentValues(defaultOptions.map((x) => x.value));
        }
    }, [defaultOptions != null]);

    const availableOptions = useMemo(() => {
        if (!queryData) {
            return [];
        }

        if (!optionsMapFn) {
            console.warn('no optionsMapFn');
            return [];
        }
        const ret = queryData
            .map(optionsMapFn)
            .filter((x) => !currentValues.includes(x.value));

        const missingCached =
            defaultOptions
                ?.filter((x) => !currentValues.includes(x.value))
                .filter((x) => !ret.some((x2) => x2.value == x.value)) || [];
        if (missingCached) {
            return [...missingCached, ...ret];
        }
        return ret;
    }, [queryData, currentValues, optionCache]);

    const activeOptions = useMemo(() => {
        const options = optionCache.filter((x) =>
            currentValues.includes(x.value)
        );
        return options;
    }, [currentValues]);

    const addItems = (values) => {
        const selectedOptions = queryData
            .map(optionsMapFn)
            .filter((x) => values.includes(x.value));

        const newCacheOptions = selectedOptions.filter(
            (x) => !optionCache.some((x2) => x2.value == x.value)
        );
        if (newCacheOptions) {
            setOptionCache([...optionCache, ...newCacheOptions]);
        }

        const uniqueValues = new Set([...currentValues, ...values]);
        setCurrentValues([...uniqueValues]);
        if (onChange) {
            onChange([...uniqueValues]);
        }
    };

    const removeItems = (values) => {
        setCurrentValues(values);
        if (onChange) {
            onChange(values);
        }
    };

    const renderQueryOption = renderOption
        ? (item) => {
              return renderOption(item);
          }
        : null;

    return (
        <Grid className={classes.root}>
            <RenderQueryList
                type='forward'
                options={availableOptions}
                isLoading={isLoading}
                onSearchChange={filterDebounce}
                renderOption={renderQueryOption}
                onTransfer={(values) => addItems(values)}
            />
            <RenderSelectedList
                type='backward'
                options={activeOptions}
                renderOption={renderQueryOption}
                onTransfer={(values) => removeItems(values)}
                form={form}
            />
        </Grid>
    );
};

const UpdateDataSetFilesModal = ({ existing, opened, close, onSuccess }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const getInitialValues = () => ({
        dataSetId: existing?.id.toString(),
        dataFileIds: existing?.files.map((x) => x.id.toString()),
    });
    const form = useForm({
        validateInputOnBlur: true,
        initialValues: getInitialValues(),

        validate: {
            dataSetId: (value) => {
                const { type, message } = genericValidator('Data set', value, {
                    required: true,
                });

                return;
            },
            dataFileIds: (value) => {
                if (!value) {
                    return 'At least one data file is required';
                }
            },
        },
    });

    useEffect(() => {
        form.setInitialValues(getInitialValues());
        form.reset();
    }, [opened]);

    const defaultOptions = useMemo(
        () =>
            existing?.files.map((file) => ({
                label: file.filename.toString(),
                value: file.id.toString(),
                status: file.status,
            })),
        [existing]
    );

    const { isPending, mutate: addOrUpdate } = useUpdateDataSetFiles();
    const onSubmit = (model) => {
        addOrUpdate(model, {
            onSuccess(data, variables, context) {
                if (onSuccess) {
                    onSuccess(data);
                }
                close();
            },
            onError(error, variables, context) {
                // setError(error);
            },
        });
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'xl'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>Update Data Set Files</Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <TransferList
                            queryHttpFn={getDatafiles}
                            queryKey={'data_files'}
                            optionsMapFn={(file) => ({
                                label: file.filename,
                                value: file.id.toString(),
                                status: file.status,
                            })}
                            defaultOptions={defaultOptions}
                            renderOption={(item, dto) => {
                                const color = getEnumColor(
                                    DATA_FILE_STATUSES,
                                    item.status
                                );
                                const label = getEnumLabel(
                                    DATA_FILE_STATUSES,
                                    item.status
                                );
                                return (
                                    <>
                                        {item.label}
                                        <Badge
                                            color={color}
                                            size={'xs'}
                                            ms={0}>
                                            {label}
                                        </Badge>
                                    </>
                                );
                            }}
                            onChange={(values) =>
                                form.setFieldValue('dataFileIds', values)
                            }
                        />

                        <Group
                            justify='flex-end'
                            mt={'xs'}>
                            <Button
                                type={'submit'}
                                size={'sm'}
                                disabled={isPending || !form.isValid()}
                                rightSection={
                                    isPending ? (
                                        <FontAwesomeIcon
                                            icon={faSpinnerThird}
                                            spin
                                        />
                                    ) : null
                                }>
                                Update
                            </Button>
                        </Group>
                    </form>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};
export default UpdateDataSetFilesModal;
